// libraries
import type { StateTree } from '@makemydeal/dr-dash-types';
import { registerScreenResolver } from '@makemydeal/dr-dash-components';
import { RouteValues } from '@makemydeal/dr-dash-store/routeConstants';
import * as routerSelectors from '@makemydeal/dr-dash-store/selectors/router';

/**
 * Injected `screen` axis for read-side telemetry (US2056179, tier 2).
 *
 * `dash-components` is a generic library and must not import dash route
 * selectors, so the host app maps the current route to a user-recognizable
 * screen label ("Dashboard", "Programs & Incentives", …). The nerdpack rolls
 * `SelectorRead` up by `section ?? screen ?? card`, so this gives base-dashboard
 * reads (which have no Interstate titled-container ancestor) a friendly group.
 */
const SCREEN_LABEL_BY_PATH: Record<string, string> = {
    [RouteValues.DASHBOARD]: 'Dashboard',
    [RouteValues.DEFAULT]: 'Dashboard',
    [RouteValues.CHANGE_VEHICLE]: 'Change Vehicle',
    [RouteValues.DEAL_HISTORY]: 'Deal History',
    [RouteValues.CREDIT_BUREAU]: 'Credit Bureau',
    [RouteValues.CREDIT_DECISION]: 'Credit Decision',
    [RouteValues.DDJ]: 'Manage Documents',
    [RouteValues.AUDIT_LOG]: 'Audit Log',
    [RouteValues.ROLE_ASSUMPTION]: 'Role Assumption',
    [RouteValues.NO_DEALERSHIPS]: 'No Dealerships',
    [RouteValues.OPEN_DEAL]: 'Open Deal'
};

/**
 * Sentinel prefix emitted as the `screen` value when a *real* route pathname has
 * no entry in `SCREEN_LABEL_BY_PATH` (DE757898). We deliberately emit a non-null,
 * path-carrying token rather than `null`: New Relic drops null-valued attributes,
 * so a `null` here would make the miss invisible and the table's drift
 * undetectable. Embedding the path makes the value self-documenting (it names the
 * exact route to add) and self-limiting (cardinality is bounded by the number of
 * forgotten routes, and each value disappears the moment a label is added). Query
 * drift / fallback rate with `screen LIKE 'unresolved:%'`.
 */
export const UNRESOLVED_SCREEN_PREFIX = 'unresolved:';

/**
 * Map a router pathname to a screen label:
 * - known route  -> its curated label;
 * - real but unmapped route -> `unresolved:<path>` sentinel (see above);
 * - no route yet (null/empty pathname) -> `null` (transient app state, not drift).
 */
export const screenLabelForPath = (pathname: string | undefined | null): string | null => {
    if (!pathname) return null;
    return SCREEN_LABEL_BY_PATH[pathname] ?? `${UNRESOLVED_SCREEN_PREFIX}${pathname}`;
};

/**
 * Register the dash screen resolver with `dash-components`. Idempotent — the
 * last registration wins. Reads the route off the passed-in state so it stays a
 * pure function of the store snapshot.
 */
export const registerDashScreenResolver = (): void => {
    registerScreenResolver((state) => screenLabelForPath(routerSelectors.getRouterPathName(state as StateTree)));
};
